import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// ContextMenu — контекст-меню действий на сообщении (§0b, FR-MSG-04..10).
// Открывается long-press на mobile / right-click на desktop — выбор триггера по useLayout()
// делает потребитель (он зовёт onOpenContextMenu из MessageBubble и ставит open=true). Здесь —
// презентационное меню: набор пунктов задаёт потребитель (items), закрытие по клику вне/Esc
// (useDismiss), danger-пункт выделен. Опц. ряд быстрых реакций сверху. Чистый presentational —
// какие действия доступны (напр. «изменить» только автору) решает потребитель списком items.
//
// useContextMenuTrigger() — хелпер: даёт правильный обработчик открытия по режиму раскладки
// (right-click на desktop, long-press на mobile) — инв.3.
import { useRef, useCallback, useState, useLayoutEffect } from 'react';
import { Icon } from '../primitives/Icon.js';
import { useDismiss } from '../primitives/useDismiss.js';
import { useLayout } from '../theme/useLayout.js';
export function ContextMenu({ open, items, anchor, onSelect, onClose, quickReactions, onQuickReact, }) {
    const menu = useRef(null);
    useDismiss({ open, onClose, ref: menu });
    // Клампинг во вьюпорт (FR-MSG-04..10): якорь = точка касания/курсора. Без коррекции меню у нижнего/
    // правого края уезжало за пределы видимой области (long-press по нижнему сообщению открывал меню под
    // экраном). Меряем реальный размер после маунта (useLayoutEffect — ДО покраски, без мигания) и:
    //   • по X — сдвигаем влево, чтобы правый край влез (с полем MARGIN);
    //   • по Y — если снизу не помещается, флипаем ВВЕРХ от якоря; иначе клампим;
    //   • слишком высокое меню (много пунктов) — ограничиваем maxHeight и включаем прокрутку.
    // Поле MARGIN учитывает и safe-area инсеты (env добавит их к 8px через реальный innerHeight/Width).
    const [pos, setPos] = useState({ top: anchor.y, left: anchor.x });
    useLayoutEffect(() => {
        if (!open)
            return;
        const el = menu.current;
        if (!el)
            return;
        const MARGIN = 8;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const { width: w, height: h } = el.getBoundingClientRect();
        let left = anchor.x;
        let top = anchor.y;
        if (left + w > vw - MARGIN)
            left = vw - MARGIN - w;
        if (left < MARGIN)
            left = MARGIN;
        if (top + h > vh - MARGIN) {
            const above = anchor.y - h; // флип вверх от якоря
            top = above >= MARGIN ? above : Math.max(MARGIN, vh - MARGIN - h);
        }
        if (top < MARGIN)
            top = MARGIN;
        setPos({ top, left });
    }, [open, anchor.x, anchor.y, items.length, quickReactions?.length]);
    if (!open)
        return null;
    const root = {
        position: 'fixed',
        top: `${pos.top}px`,
        left: `${pos.left}px`,
        zIndex: 'var(--z-menu)',
        minWidth: '200px',
        maxWidth: '260px',
        maxHeight: 'calc(100vh - 16px)',
        background: 'var(--color-surface)',
        color: 'var(--color-text)',
        borderRadius: 'var(--radius-card)',
        boxShadow: 'var(--shadow-menu)',
        padding: 'var(--space-1)',
        overflow: 'hidden auto',
    };
    return (_jsxs("div", { ref: menu, role: "menu", "data-context-menu": "", "data-layout-trigger": "auto", style: root, children: [quickReactions && quickReactions.length > 0 ? (_jsx("div", { "data-quick-reactions": "", style: {
                    display: 'flex',
                    gap: '4px',
                    padding: 'var(--space-1) var(--space-2)',
                    borderBottom: '1px solid var(--color-divider)',
                    marginBottom: '4px',
                }, children: quickReactions.map((emoji) => (_jsx("button", { type: "button", "data-quick-reaction": emoji, "aria-label": `Реакция ${emoji}`, onClick: () => {
                        onQuickReact?.(emoji);
                        onClose();
                    }, style: {
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: 'var(--font-lg)',
                        padding: '2px 4px',
                        borderRadius: 'var(--radius-control)',
                    }, children: emoji }, emoji))) })) : null, items.map((item) => (_jsxs("button", { type: "button", role: "menuitem", "data-menu-item": item.id, "data-danger": item.danger ? '' : undefined, disabled: item.disabled, onClick: () => {
                    if (item.disabled)
                        return;
                    onSelect(item.id);
                    onClose();
                }, style: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    width: '100%',
                    padding: 'var(--space-2) var(--space-3)',
                    border: 'none',
                    background: 'transparent',
                    borderRadius: 'var(--radius-control)',
                    cursor: item.disabled ? 'default' : 'pointer',
                    opacity: item.disabled ? 0.5 : 1,
                    color: item.danger ? 'var(--color-danger)' : 'var(--color-text)',
                    fontSize: 'var(--font-md)',
                    textAlign: 'left',
                }, children: [item.icon ? _jsx(Icon, { name: item.icon, size: 18 }) : null, _jsx("span", { children: item.label })] }, item.id)))] }));
}
/**
 * Хелпер-хук: по текущему режиму раскладки (useLayout) возвращает обработчики, которые открывают
 * контекст-меню правильным жестом — right-click на desktop, long-press на mobile (инв.3).
 * @param open колбэк открытия с координатами якоря.
 * @param longPressMs порог удержания для mobile (дефолт 450мс).
 */
export function useContextMenuTrigger(open, longPressMs = 450) {
    const mode = useLayout();
    const timer = useRef(null);
    const lastPoint = useRef({ x: 0, y: 0 });
    const clear = useCallback(() => {
        if (timer.current !== null) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);
    const handlers = {
        onContextMenu: (e) => {
            // right-click активен только на desktop (инв.3)
            if (mode !== 'desktop')
                return;
            e.preventDefault();
            open({ x: e.clientX, y: e.clientY });
        },
        onPointerDown: (e) => {
            // long-press активен только на mobile (инв.3)
            if (mode !== 'mobile')
                return;
            lastPoint.current = { x: e.clientX, y: e.clientY };
            clear();
            timer.current = setTimeout(() => open(lastPoint.current), longPressMs);
        },
        onPointerUp: clear,
        onPointerLeave: clear,
    };
    return { mode, handlers };
}
//# sourceMappingURL=ContextMenu.js.map